import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const BoneGrafting = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Bone Grafting Fort Worth TX | Dental Bone Graft Surgery | Fort Worth Dental</title>
        <meta name="description" content="Expert bone grafting in Fort Worth TX for dental implants. Jawbone restoration surgery by experienced oral surgeons. Preparing patients for successful dental implants. Call (817) 985-3337." />
        <meta name="keywords" content="bone grafting fort worth, dental bone graft, jawbone restoration, dental implant preparation, oral surgery fort worth tx, bone graft surgery" />
        <link rel="canonical" href="https://fortworthdental.com/bone-grafting" />
        <meta property="og:title" content="Bone Grafting Fort Worth TX | Dental Bone Graft Surgery" />
        <meta property="og:description" content="Expert bone grafting for dental implants by Fort Worth Dental. Jawbone restoration surgery." />
        <meta property="og:url" content="https://fortworthdental.com/bone-grafting" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(/images/services/dental-implant-hero.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Bone Grafting in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Restoring Jaw Bone for Dental Implants</p>
            <p className="text-sm mt-4 opacity-90">Dental Bone Graft Surgery | Jawbone Restoration | Implant Preparation</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Bone Grafting */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/bone-grafting-new-1.jpg" 
                  alt="Bone grafting procedure for dental implants at Fort Worth Dental TX" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Bone Grafting?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Bone grafting is used when the amount of bone in a patient's jaw needs to be increased. This is commonly done to provide a stable bone infrastructure for the implanting of dental implants. In some cases, the bone grafting is done as a part of the dental implant procedure itself. In other cases, the bone grafting may be a separate procedure with a separate appointment. In either case, bone material is added to the existing infrastructure in order to strengthen it.
                </p>
              </div>
            </div>
          </section>

          {/* Where Does The Dentist Get Bone Material */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Where Does The Dentist Get Bone Material For Bone Grafting?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Bone grafting material is found in several places. It is common and preferred to retrieve the bone grafting material from the patient's own mouth. When that source is not possible, the dentist may pursue a few other options. He may get freeze-dried human bone from a tissue bank. It is also possible to get processed bone tissue from animals. In some cases, a dentist may use a bone substitute that has been developed from mineral sources. Whenever bone tissue is obtained from sources other than the patient's own mouth, there are procedures and safeguards similar to those used for a blood bank. Many safety procedures and safeguards are used to protect the safety of the patient.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/bone-grafting-new-2.jpg" 
                  alt="Dental bone grafting material and jawbone restoration in Fort Worth Texas" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* When Is Bone Grafting Done */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/bone-grafting-new-3.jpg" 
                  alt="When bone grafting is needed before dental implant surgery Fort Worth" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">When Is Bone Grafting Done?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Sometimes the jawbone does not have sufficient bone structure remaining to support dental implants. Bone grafting techniques will be used to build a dental ridge that is capable of supporting these implanted teeth for the long term. At times this procedure may be implemented on a smaller scale just to provide bone cover for the sides of the dental implants.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Bone Grafting?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team uses the latest technology and techniques to provide comfortable, effective bone grafting treatment.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Board Certified</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Bone Grafting Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about bone grafting in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Bone Grafting Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default BoneGrafting;
